import api from './api';
import { AxiosResponse } from 'axios';

export interface Location {
  id: string;
  locationCode: string;
  description: string;
  warehouseCode?: string;
  capacity: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface LocationStats {
  total: number;
  active: number;
  inactive: number;
  totalCapacity: number;
}

export interface Warehouse {
  warehouseCode: string;
  description: string;
  locationCode?: string;
}

export interface GetLocationsParams {
  page?: number;
  limit?: number;
  search?: string;
  active?: boolean;
}

export interface CreateLocationRequest {
  locationCode: string;
  description: string;
  warehouseCode?: string;
  capacity?: number;
  isActive?: boolean;
}

export interface LocationResponse {
  status: string;
  data: {
    location: Location;
  };
  message?: string;
}

export interface LocationsResponse {
  status: string;
  data: {
    locations: Location[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
  message?: string;
}

const locationService = {
  /**
   * Get all locations with pagination and filters
   */
  async getLocations(params: GetLocationsParams = {}): Promise<LocationsResponse> {
    try {
      const response: AxiosResponse<LocationsResponse> = await api.get('/locations', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch locations');
    }
  },

  /**
   * Get a single location by ID
   */
  async getLocationById(id: string): Promise<Location> {
    try {
      const response: AxiosResponse<LocationResponse> = await api.get(`/locations/${id}`);
      return response.data.data.location;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || `Failed to fetch location ${id}`);
    }
  },

  /**
   * Create a new location
   */
  async createLocation(locationData: CreateLocationRequest): Promise<Location> {
    try {
      const response: AxiosResponse<LocationResponse> = await api.post('/locations', locationData);
      return response.data.data.location;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create location');
    }
  },

  /**
   * Update an existing location
   */
  async updateLocation(id: string, locationData: Partial<CreateLocationRequest>): Promise<Location> {
    try {
      const response: AxiosResponse<LocationResponse> = await api.put(`/locations/${id}`, locationData);
      return response.data.data.location;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || `Failed to update location ${id}`);
    }
  },

  /**
   * Toggle location active/inactive status
   */
  async toggleLocationStatus(id: string): Promise<Location> {
    try {
      const response: AxiosResponse<LocationResponse> = await api.patch(`/locations/${id}/toggle-status`);
      return response.data.data.location;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || `Failed to toggle location status`);
    }
  },

  /**
   * Delete a location (soft delete - deactivates it)
   */
  async deleteLocation(id: string): Promise<void> {
    try {
      await api.delete(`/locations/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || `Failed to delete location ${id}`);
    }
  },

  /**
   * Get location statistics
   */
  async getLocationStats(): Promise<LocationStats> {
    try {
      const response = await api.get('/locations/stats/summary');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch location statistics');
    }
  },

  /**
   * Get all warehouses
   */
  async getWarehouses(): Promise<Warehouse[]> {
    try {
      const response = await api.get('/warehouses');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch warehouses');
    }
  },
};

export default locationService;